import { useMemo, useState } from 'react';
import { haptics } from '../../telegram';
import type { QualChartPoint } from '../../api/types';

const W = 296;
const H = 140;
const PAD_L = 10;
const PAD_R = 56;
const PAD_T = 14;
const PAD_B = 22;

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Мини-график отбора (Q2/Б): линия от старта по турнирам, пропуски — «✕» на
 * оси X, пунктиры «START · СТАРТ» и «#N · рейтинг отсечки». Геометрия — как у
 * RatingChart (min-span, headroom, широкие хит-зоны), префикс стилей qc-.
 */
export function QualificationChart({
  points,
  startRating,
  cutoff,
  tickets,
}: {
  points: QualChartPoint[];
  startRating: number;
  cutoff: number | null;
  tickets: number;
}) {
  const [sel, setSel] = useState<number | null>(null);
  const games = points; // и game, и miss участвуют в оси X

  const geo = useMemo(() => {
    if (games.length < 2) return null;
    const values = games.map((h) => h.value).concat([startRating]);
    if (cutoff !== null) values.push(cutoff);
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (max - min < 30) {
      const mid = (max + min) / 2;
      min = mid - 15;
      max = mid + 15;
    }
    const span = max - min;
    min -= span * 0.12;
    max += span * 0.12;
    const x = (i: number) => PAD_L + (i * (W - PAD_L - PAD_R)) / (games.length - 1);
    const y = (v: number) => PAD_T + ((max - v) * (H - PAD_T - PAD_B)) / (max - min);
    return { min, max, x, y };
  }, [games, startRating, cutoff]);

  if (!geo) return null;
  const { x, y } = geo;
  const selIdx = sel ?? games.length - 1;
  const p = games[selIdx];
  const gameIdx = games.map((g, i) => ({ g, i })).filter(({ g }) => g.kind === 'game');
  const path = gameIdx
    .map(({ g, i }, k) => `${k === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(g.value).toFixed(1)}`)
    .join(' ');
  const tipLeftPct = (x(selIdx) / W) * 100;

  return (
    <div className="qc-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="qc-chart-svg">
        <line x1={PAD_L} x2={W - PAD_R + 8} y1={y(startRating)} y2={y(startRating)} className="qc-threshold" strokeDasharray="3 4" />
        <text x={W - PAD_R + 12} y={y(startRating) + 2.5} className="qc-threshold-label">
          {startRating} · СТАРТ
        </text>
        {cutoff !== null && (
          <>
            <line x1={PAD_L} x2={W - PAD_R + 8} y1={y(cutoff)} y2={y(cutoff)} className="qc-threshold qc-threshold--cutoff" strokeDasharray="3 4" />
            <text x={W - PAD_R + 12} y={y(cutoff) + 2.5} className="qc-threshold-label qc-threshold-label--cutoff">
              #{tickets} · {cutoff}
            </text>
          </>
        )}
        <path d={path} className="qc-line" fill="none" />
        {games.map((h, i) => (
          <g key={`${h.gameId}-${h.kind}-${i}`}>
            {h.kind === 'game' ? (
              <>
                {i === selIdx && <circle cx={x(i)} cy={y(h.value)} r={8} className="qc-halo" />}
                <circle
                  cx={x(i)}
                  cy={y(h.value)}
                  r={i === games.length - 1 ? 4.5 : 2.6}
                  className="qc-dot"
                />
              </>
            ) : (
              <text x={x(i)} y={H - 8} textAnchor="middle" className="qc-miss">
                ✕
              </text>
            )}
            <rect
              x={x(i) - 7}
              y={0}
              width={14}
              height={H}
              fill="transparent"
              onClick={() => {
                haptics.tap();
                setSel(i);
              }}
            />
          </g>
        ))}
        <text x={PAD_L} y={H - 6} className="qc-axis">
          НЕД. 1
        </text>
        <text x={W - PAD_R + 8} y={H - 6} textAnchor="end" className="qc-axis">
          СЕЙЧАС
        </text>
      </svg>
      <div className="qc-tooltip" style={{ left: `clamp(14%, ${tipLeftPct}%, 86%)` }} key={`${p.gameId}-${p.kind}`}>
        {p.kind === 'miss' ? (
          <>
            <div className="t">Пропуск {fmtDate(p.date)}</div>
            <div className="d qc-neg">{p.delta === 0 ? 'без штрафа' : p.delta}</div>
          </>
        ) : (
          <>
            <div className="t">Турнир {fmtDate(p.date)}</div>
            <div className="d">
              {p.place !== null && p.fieldSize !== null ? `${p.place} место из ${p.fieldSize} · ` : ''}
              <span className={p.delta >= 0 ? 'up' : 'dn'}>{p.delta >= 0 ? `+${p.delta}` : p.delta}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
